/**
 * Adapts a reschedule page's appointment/visit into `SchedulePicker` props.
 *
 * Returns `null` when the visit lacks a location, a service, or coordinates —
 * `/api/v1/therapists/available` cannot be queried without them, so the caller
 * renders the legacy flow instead. Coordinates of exactly 0 count as absent:
 * the app uses 0 as its "no coordinate" fallback, and no visit address sits on
 * Null Island.
 */
export type ReschedulePickerVisit = {
	locationId?: string | number | null;
	serviceId?: string | number | null;
	latitude?: number | null;
	longitude?: number | null;
};

/**
 * The page's `optionsData.apptDateTime`. Nulls mean "unbounded" — which is the
 * normal case when `ENABLE_PAST_DATE_RESCHEDULING_BYPASS` is on. Accepts a Date
 * or an ISO string: the single-reschedule hook parses the prop into a Date,
 * while bulk passes the raw string through.
 */
export type ReschedulePickerBounds = {
	min?: Date | string | null;
	max?: Date | string | null;
};

export type ReschedulePickerProps = {
	latitude: number;
	longitude: number;
	locationId: string;
	serviceId: string;
	minDate?: Date;
	maxDate?: Date;
};

/** An address the picker could query from — a visit snapshot or a live one. */
export type PickerAddress = {
	locationId?: string | number | null;
	latitude?: number | null;
	longitude?: number | null;
};

function isUsableAddress(address: PickerAddress | null | undefined): boolean {
	return !!address?.locationId && !!address?.latitude && !!address?.longitude;
}

/**
 * Picks the first address the picker can actually query from.
 *
 * The visit-address snapshot (`address_history`) is the truth for where the
 * visit happens, so it wins — but it is only taken when the appointment is
 * created, and only when the patient had an active address then. Older
 * appointments have none, so we fall back to the patient's current active
 * address rather than hiding the calendar. Mirrors the same fallback in
 * PreparationBulkRescheduleService#serialize_order_detail.
 */
export function resolveVisitAddress(
	...candidates: (PickerAddress | null | undefined)[]
): PickerAddress | null {
	return candidates.find(isUsableAddress) ?? null;
}

function toBound(value: Date | string | null | undefined): Date | undefined {
	if (!value) return undefined;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? undefined : date;
}

export function buildReschedulePickerProps(
	visit: ReschedulePickerVisit,
	bounds?: ReschedulePickerBounds,
): ReschedulePickerProps | null {
	const locationId = visit.locationId ? String(visit.locationId) : "";
	const serviceId = visit.serviceId ? String(visit.serviceId) : "";
	const { latitude, longitude } = visit;

	if (!locationId || !serviceId || !latitude || !longitude) return null;

	return {
		latitude,
		longitude,
		locationId,
		serviceId,
		minDate: toBound(bounds?.min),
		maxDate: toBound(bounds?.max),
	};
}
